/**
 * Convert KaTeX math in an inline-choice option to readable plain text.
 *
 * Inline-choice (SHSAT type 9) options render inside native ``<option>``
 * elements, which display text only — KaTeX cannot run there, so a stored
 * ``$9\sqrt{2}$`` would leak through literally. Both student players (adaptive
 * and legacy) pass the option label through this; the option ``value`` keeps
 * the raw stored string, so server-side string-equality grading against
 * ``QuestionInlineChoiceAnswer.answer`` is unaffected.
 *
 *   ``$\$90$``          -> ``$90``     (literal dollar amounts)
 *   ``$90$``            -> ``90``      (plain number wrapped in math)
 *   ``$\frac{2}{9}$``   -> ``2/9``     (also ``{5\over36}``, MathLive's ``\frac12``)
 *   ``$2\frac{1}{2}$``  -> ``2 1/2``   (mixed number keeps its space)
 *   ``$\frac{x+1}{2}$`` -> ``(x+1)/2`` (multi-term parts are parenthesized)
 *   ``$9\sqrt{2}$``     -> ``9√2``     (also MathLive's brace-less ``\sqrt2``)
 *   ``$x^2 \le 9$``     -> ``x² ≤ 9``
 *
 * Like KaTeX's auto-render, only text inside ``$...$`` is treated as math;
 * text outside the delimiters is shown as-is. Each math segment is converted on
 * its own, so ``$1\over2$ and $3\over4$`` keeps the words between them.
 *
 * The literal-``$`` case is the tricky one: a naive ``$...$`` strip matches the
 * ``$\$`` prefix first and leaves a trailing ``$``. Fix is to hide ``\$`` behind
 * a placeholder before finding math segments, then restore — this also covers
 * the ``$\$N\$ ...$`` "two dollar amounts in one math block" shape.
 *
 * Fractions stay inline (``a/b``); a stacked fraction needs a custom dropdown
 * component. See docs/bugfix/20261030-shsat-inline-choice-dropdown-math-plan.md.
 */

const DOLLAR_PLACEHOLDER = '\x00';
const LBRACE_PLACEHOLDER = '\x01';
const RBRACE_PLACEHOLDER = '\x02';

/** ``\name`` -> character, matched only when not followed by another letter. */
const SYMBOLS: ReadonlyArray<[RegExp, string]> = ([
    ['times', '×'],
    ['cdot', '·'],
    ['div', '÷'],
    ['leq', '≤'],
    ['le', '≤'],
    ['geq', '≥'],
    ['ge', '≥'],
    ['neq', '≠'],
    ['ne', '≠'],
    ['pm', '±'],
    ['pi', 'π'],
    ['degree', '°'],
] as const).map(([name, char]): [RegExp, string] => [new RegExp(`\\\\${name}(?![a-zA-Z])`, 'g'), char]);

const SUPERSCRIPTS: Readonly<Record<string, string>> = {
    '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
    '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹', '-': '⁻',
};

const ROOT_SIGNS: Readonly<Record<string, string>> = { '2': '√', '3': '∛', '4': '∜' };

/** A term that reads unambiguously without parentheses: ``12``, ``x``, ``π``, ``√2``. */
const BARE_TERM = /^-?(\d+(\.\d+)?|[a-zA-Zπ]|√(\d+(\.\d+)?|[a-zA-Z]|\([^()]*\)))$/;

/** Characters that would visually run into an inline fraction or radical. */
const RUNS_ON_BEFORE = /[0-9a-zA-Zπ)²³]$/;
const RUNS_ON_AFTER = /^[0-9a-zA-Zπ(√.]/;

/** Max passes for nested ``\sqrt``/``\frac`` (e.g. ``\frac{\sqrt{3}}{2}``). */
const MAX_NESTING_PASSES = 5;

const wrapTerm = (t: string): string => (BARE_TERM.test(t) ? t : `(${t})`);

/**
 * ``num/den`` with parts parenthesized when needed. ``before``/``after`` are the
 * neighbouring text, so ``2\frac{1}{2}`` reads as ``2 1/2`` (not ``21/2``) and
 * ``\frac{2}{3}\pi`` as ``(2/3)π`` (not ``2/3π``).
 */
function fractionLabel(num: string, den: string, before: string, after: string): string {
    const n = num.trim();
    const d = den.trim();
    const label = `${wrapTerm(n)}/${wrapTerm(d)}`;
    if (/\d$/.test(before) && /^\d+$/.test(n) && /^\d+$/.test(d)) return ` ${label}`;
    return RUNS_ON_BEFORE.test(before) || RUNS_ON_AFTER.test(after) ? `(${label})` : label;
}

/** ``√x``; parenthesized when the radicand is long or a digit follows (``√(2)3``). */
function rootLabel(radicand: string, index: string, after: string): string {
    const r = radicand.trim();
    const sign = ROOT_SIGNS[index] ?? `${index}√`;
    const bare = /^(\d+(\.\d+)?|[a-zA-Zπ])$/.test(r) && !/^[0-9.]/.test(after);
    return bare ? `${sign}${r}` : `${sign}(${r})`;
}

/** ``replace`` callback args end with ``offset, whole``; split the neighbours out. */
function neighbours(args: unknown[], matchLength: number): [string, string] {
    const offset = args[args.length - 2] as number;
    const whole = args[args.length - 1] as string;
    return [whole.slice(0, offset), whole.slice(offset + matchLength)];
}

/** Innermost-first: each pass resolves constructs whose arguments hold no braces. */
function collapseRootsAndFractions(s: string): string {
    let out = s;
    for (let i = 0; i < MAX_NESTING_PASSES; i++) {
        const next = out
            .replace(/\\sqrt\s*(?:\[\s*(\d+)\s*\])?\s*\{([^{}]*)\}/g, (m, idx: string | undefined, r: string, ...rest) =>
                rootLabel(r, idx ?? '2', neighbours(rest, m.length)[1]))
            .replace(/\\[dt]?frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g, (m, n: string, d: string, ...rest) =>
                fractionLabel(n, d, ...neighbours(rest, m.length)))
            .replace(/\{([^{}]*)\\over([^{}]*)\}/g, (m, n: string, d: string, ...rest) =>
                fractionLabel(n, d, ...neighbours(rest, m.length)));
        if (next === out) break;
        out = next;
    }
    return out;
}

function superscript(digits: string): string {
    return Array.from(digits, (c) => SUPERSCRIPTS[c]).join('');
}

/** Convert the body of one ``$...$`` segment. */
function mathToText(math: string): string {
    const simplified = SYMBOLS.reduce(
        (acc, [re, char]) => acc.replace(re, char),
        math
            .replace(/\\\{/g, LBRACE_PLACEHOLDER)
            .replace(/\\\}/g, RBRACE_PLACEHOLDER)
            .replace(/\\(?:text|mathrm|textrm)\s*\{([^{}]*)\}/g, '$1')
            .replace(/\\(?:left|right)(?![a-zA-Z])\s*/g, '')
            .replace(/\\!/g, '')
            .replace(/\\[,;: ]/g, ' ')
            .replace(/\\%/g, '%')
            .replace(/\^\s*\{?\s*\\circ\s*\}?/g, '°'),
    );

    const collapsed = collapseRootsAndFractions(simplified
        // MathLive drops braces on single-character arguments (``\sqrt2``,
        // ``\frac12``); restore them so nesting resolves in one place.
        .replace(/\\sqrt\s*([0-9a-zA-Z])/g, '\\sqrt{$1}')
        .replace(/\\([dt]?frac)\s*([0-9a-zA-Z])\s*([0-9a-zA-Z])/g, '\\$1{$2}{$3}'));

    // A top-level ``\over`` splits the whole segment: ``$a \over b + c$`` is a/(b+c).
    const overAt = collapsed.indexOf('\\over');
    const overSplit = overAt === -1
        ? collapsed
        : fractionLabel(collapsed.slice(0, overAt), collapsed.slice(overAt + '\\over'.length), '', '');

    return overSplit
        .replace(/\^\s*\{\s*(-?\d+)\s*\}/g, (_, d: string) => superscript(d))
        .replace(/\^\s*(\d)/g, (_, d: string) => superscript(d))
        .replace(/\^\s*\{([^{}]*)\}/g, (_, e: string) => `^${wrapTerm(e.trim())}`)
        // Remaining grouping braces are invisible in KaTeX output.
        .replace(/[{}]/g, '')
        // Math mode collapses source whitespace (``\$5 \text{ and } \$6``).
        .replace(/\s+/g, ' ')
        .replace(new RegExp(LBRACE_PLACEHOLDER, 'g'), '{')
        .replace(new RegExp(RBRACE_PLACEHOLDER, 'g'), '}');
}

export function plainizeMath(s: string): string {
    return (s || '')
        // Stash ``\$`` so it doesn't confuse the math-segment match.
        .replace(/\\\$/g, DOLLAR_PLACEHOLDER)
        // Unescape ``\\`` (backslash) before restoring ``$`` so a sequence
        // like ``\\\$`` doesn't get mangled.
        .replace(/\\\\/g, '\\')
        .replace(/\$([^$]*?)\$/g, (_, math: string) => mathToText(math))
        .replace(new RegExp(DOLLAR_PLACEHOLDER, 'g'), '$')
        .trim();
}
